import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { message } from '@maxtropy/components';
import '@/components/u3d-render-streaming';
import styles from './index.module.scss';
import { DEFAULT_UNITY_INITIAL_CAMERA_DATA } from './cameraConfig';
import type { UnityCameraData } from './cameraConfig';
import {
  cleanupUnityBusinessPayloads,
  cleanupUnityBusinessPayloadsExcept,
  setActiveUnityBusinessCleanupCaller,
  type BusinessCleanupKey,
} from './businessCleanup';

export type { UnityCameraData, UnityCameraPoint } from './cameraConfig';

const DEFAULT_UNITY_RS_URL = '192.168.2.123:88';
const DEFAULT_UNITY_PORTAL_URL = 'http://ykdc.chinasws.com/screen/unity.html';
const DEFAULT_UNITY_PORTAL_SEND_DELAY = 3000;
const UNITY_CLICK_EVENT = 'unity-click';
const EMPTY_BUSINESS_CLEANUP_KEYS: BusinessCleanupKey[] = [];

export type UnityPayload = { type: string; data?: unknown };

export type UnityEnvelope = {
  type: 'request' | 'response' | 'notify';
  uuid?: string;
  ts?: number;
  success?: boolean;
  payload?: any;
  error?: string;
};

export type RenderStreamingVideoElement = HTMLVideoElement & {
  sendRequestMsg?: (payload: UnityPayload, onNotify?: (payload: any) => void) => Promise<unknown>;
  projectToWorld?: (coords: [number, number, number?]) => [number, number, number];
  closeWindow?: () => void;
};

export type UnityCall = (payload: UnityPayload, notify?: (value: any) => void) => Promise<unknown>;
export type UnityClickDetail = { data?: { name?: string; [key: string]: any }; uuid?: string };

type PendingHandler = {
  resolve: (value: unknown) => void;
  reject: (reason?: any) => void;
  notify?: (value: any) => void;
};

type RenderStreamingVideoCache = {
  el: RenderStreamingVideoElement;
  portalReady: boolean;
  portalUrl?: string;
};

const renderStreamingVideoCache = new Map<string, RenderStreamingVideoCache>();

interface UnityRenderMapProps {
  className?: string;
  children?: React.ReactNode;
  rsUrl?: string;
  portalUrl?: string;
  portalSendDelay?: number;
  initialCameraData?: UnityCameraData[] | null;
  businessCleanupKeys?: BusinessCleanupKey[];
  enableBridge?: boolean;
  onUnityClick?: (detail: UnityClickDetail) => void;
  onReady?: (helpers: { callUnity: UnityCall; videoElement: RenderStreamingVideoElement }) => void | Promise<void>;
}

function createUuid() {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }

  return `unity-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

const UnityRenderMap: FC<UnityRenderMapProps> = props => {
  const {
    className,
    children,
    rsUrl = DEFAULT_UNITY_RS_URL,
    portalUrl = DEFAULT_UNITY_PORTAL_URL,
    portalSendDelay = DEFAULT_UNITY_PORTAL_SEND_DELAY,
    initialCameraData = DEFAULT_UNITY_INITIAL_CAMERA_DATA,
    businessCleanupKeys = EMPTY_BUSINESS_CLEANUP_KEYS,
    enableBridge = true,
    onUnityClick,
    onReady,
  } = props;
  const unityContainerRef = useRef<HTMLDivElement | null>(null);
  const unityVideoRef = useRef<RenderStreamingVideoElement | null>(null);
  const businessCleanupKeysRef = useRef<BusinessCleanupKey[]>(businessCleanupKeys);
  const pendingRef = useRef<Map<string, PendingHandler>>(new Map());
  const [unityReady, setUnityReady] = useState(false);

  useEffect(() => {
    businessCleanupKeysRef.current = businessCleanupKeys;
  }, [businessCleanupKeys]);

  const sendInitialCamera = useCallback(
    (el: RenderStreamingVideoElement) => {
      if (!initialCameraData?.length) return Promise.resolve();

      console.info('[UnityMap] setting initial camera', initialCameraData);
      const request = el.sendRequestMsg?.({
        type: 'setCamera',
        data: initialCameraData,
      });

      if (!request) return Promise.reject(new Error('Unity connection is not ready when setting camera'));

      return request.then(() => {
        console.info('[UnityMap] initial camera set');
      });
    },
    [initialCameraData]
  );

  const callWeb = useCallback(
    (obj: UnityEnvelope) => {
      if (!obj) return;
      console.info('[UnityMap] callWeb received', obj);

      if (obj.type === 'response') {
        const uuid = obj.uuid ?? '';
        const handler = pendingRef.current.get(uuid);
        if (!handler) return;
        pendingRef.current.delete(uuid);
        if (obj.success) {
          handler.resolve(obj.payload);
        } else {
          handler.reject(obj.error ?? 'Unity response failed');
        }
        return;
      }

      if (obj.type === 'notify') {
        const uuid = obj.uuid ?? '';
        const handler = pendingRef.current.get(uuid);
        if (handler?.notify) {
          handler.notify(obj.payload);
          return;
        }
      }

      if (!obj.payload?.type) return;

      const detail = { data: obj.payload.data, uuid: obj.uuid };
      const videoElement = unityVideoRef.current;
      const legacyEventName = `unity_${obj.payload.type}`;
      const eventName = obj.payload.type === 'click' ? UNITY_CLICK_EVENT : legacyEventName;

      if (obj.payload.type === 'cameraChange') {
        console.info('[UnityMap] cameraChange cameraPoints', obj.payload.data?.cameraPoints, obj.payload.data);
      }

      if (obj.payload.type === 'click') {
        console.info('[UnityMap] unity-click detail', detail);
        message.success('获取点击事件成功');
        onUnityClick?.(detail);
      }

      videoElement?.dispatchEvent(
        new CustomEvent(eventName, {
          detail,
        })
      );
      window.dispatchEvent(
        new CustomEvent(obj.payload.type, {
          detail: obj.payload.data,
        })
      );
    },
    [onUnityClick]
  );

  const callUnity = useCallback<UnityCall>((payload, notify) => {
    const globalCallUnity = (window as any).callUnity as undefined | ((obj: UnityEnvelope) => void);
    if (typeof globalCallUnity === 'function') {
      return new Promise((resolve, reject) => {
        const uuid = createUuid();
        pendingRef.current.set(uuid, { resolve, reject, notify });
        try {
          globalCallUnity({
            type: 'request',
            uuid,
            ts: Date.now(),
            payload,
          });
        } catch (err: any) {
          pendingRef.current.delete(uuid);
          reject(err?.message ?? err);
        }
      });
    }

    const el = unityVideoRef.current;
    if (!el?.sendRequestMsg) return Promise.reject(new Error('Unity connection is not ready'));
    return el.sendRequestMsg(payload, notify);
  }, []);

  useEffect(() => {
    const container = unityContainerRef.current;
    if (!container) return;

    let cache = renderStreamingVideoCache.get(rsUrl);
    if (!cache) {
      const el = document.createElement('video', { is: 'render-streaming' }) as RenderStreamingVideoElement;
      el.setAttribute('audio', '');
      el.setAttribute('rs', rsUrl);
      el.disablePictureInPicture = true;
      cache = {
        el,
        portalReady: false,
      };
      renderStreamingVideoCache.set(rsUrl, cache);
      console.info('[UnityMap] render-streaming video created', { rsUrl });
    } else {
      console.info('[UnityMap] render-streaming video reused', { rsUrl, portalReady: cache.portalReady });
    }

    const { el } = cache;
    el.style.width = '100%';
    el.style.height = '100%';
    el.style.objectFit = 'cover';
    el.style.background = 'transparent';

    container.innerHTML = '';
    container.appendChild(el);
    unityVideoRef.current = el;

    return () => {
      if (container.contains(el)) {
        container.removeChild(el);
      }
      unityVideoRef.current = null;
      pendingRef.current.forEach(handler => {
        handler.reject('Unity component unmounted');
      });
      pendingRef.current.clear();
    };
  }, [rsUrl]);

  useEffect(() => {
    const el = unityVideoRef.current;
    if (!el) return;
    let portalTimer: number | undefined;
    const cache = renderStreamingVideoCache.get(rsUrl);

    const handleVideoReady = () => {
      if (cache?.portalReady && cache.portalUrl === portalUrl) {
        console.info('[UnityMap] portalUrl already sent, skip bootstrap', { portalUrl });
        sendInitialCamera(el)
          .catch(error => {
            console.error('[UnityMap] initial camera send failed', error);
          })
          .finally(() => {
            console.info('[UnityMap] unity ready for business sync');
            setUnityReady(true);
          });
        return;
      }

      console.info('[UnityMap] rs-video-ready, scheduling portalUrl', {
        delay: portalSendDelay,
        portalUrl,
        initialCameraData,
      });
      portalTimer = window.setTimeout(() => {
        console.info('[UnityMap] sending portalUrl', { portalUrl });
        const portalRequest = el.sendRequestMsg?.({
          type: 'portalUrl',
          data: portalUrl,
        });

        if (!portalRequest) {
          console.warn('[UnityMap] sendRequestMsg is not ready when sending portalUrl');
          if (cache) {
            cache.portalReady = true;
            cache.portalUrl = portalUrl;
          }
          setUnityReady(true);
          return;
        }

        portalRequest
          .then(() => {
            console.info('[UnityMap] portalUrl sent');
            if (cache) {
              cache.portalReady = true;
              cache.portalUrl = portalUrl;
            }
            return sendInitialCamera(el);
          })
          .catch(error => {
            console.error('[UnityMap] portalUrl or initial camera send failed', error);
          })
          .finally(() => {
            console.info('[UnityMap] unity ready for business sync');
            setUnityReady(true);
          });
      }, portalSendDelay);
    };

    const handleVideoDisconnected = () => {
      console.warn('[UnityMap] rs-disconnected');
    };

    el.addEventListener('rs-video-ready', handleVideoReady as EventListener);
    el.addEventListener('rs-disconnected', handleVideoDisconnected as EventListener);

    if (cache?.portalReady && cache.portalUrl === portalUrl) {
      handleVideoReady();
    }

    return () => {
      if (portalTimer) {
        window.clearTimeout(portalTimer);
        console.info('[UnityMap] cleared portalUrl timer');
      }
      setUnityReady(false);
      el.removeEventListener('rs-video-ready', handleVideoReady as EventListener);
      el.removeEventListener('rs-disconnected', handleVideoDisconnected as EventListener);
    };
  }, [portalSendDelay, portalUrl, rsUrl, sendInitialCamera]);

  useEffect(() => {
    const el = unityVideoRef.current;
    if (!el) return;

    const handleLegacyUnityClick = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      console.info('[UnityMap] unity-click detail', detail);
      onUnityClick?.(detail);
      el.dispatchEvent(
        new CustomEvent(UNITY_CLICK_EVENT, {
          detail,
        })
      );
    };

    el.addEventListener('unity_click', handleLegacyUnityClick as EventListener);
    return () => {
      el.removeEventListener('unity_click', handleLegacyUnityClick as EventListener);
    };
  }, [onUnityClick, rsUrl]);

  useEffect(() => {
    if (!enableBridge) return undefined;
    const prevCallWeb = (window as any).callWeb;
    const callWebHandler = (obj: UnityEnvelope) => {
      callWeb(obj);
    };
    (window as any).callWeb = callWebHandler;
    console.info('[UnityMap] callWeb bridge installed');
    return () => {
      if ((window as any).callWeb === callWebHandler) {
        (window as any).callWeb = prevCallWeb;
        console.info('[UnityMap] callWeb bridge restored');
      }
    };
  }, [callWeb, enableBridge]);

  useEffect(() => {
    if (!enableBridge) return undefined;

    const handleEmbeddedBridgeEvent = (obj: UnityEnvelope) => {
      if (!obj?.payload?.type) return;
      console.info('[UnityMap] embedded bridge event received', obj);
      callWeb(obj);
    };

    let channel: BroadcastChannel | null = null;
    if (typeof window.BroadcastChannel === 'function') {
      channel = new BroadcastChannel('unity-bridge');
      channel.onmessage = event => {
        handleEmbeddedBridgeEvent(event.data);
      };
      console.info('[UnityMap] embedded BroadcastChannel listener installed');
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== '__UNITY_EMBEDDED_BRIDGE_EVENT__' || !event.newValue) return;
      try {
        handleEmbeddedBridgeEvent(JSON.parse(event.newValue));
      } catch (error) {
        console.warn('[UnityMap] parse embedded bridge storage event failed', error);
      }
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      channel?.close();
      window.removeEventListener('storage', handleStorage);
    };
  }, [callWeb, enableBridge]);

  useEffect(() => {
    const el = unityVideoRef.current;
    if (!unityReady || !el) return;
    let canceled = false;
    setActiveUnityBusinessCleanupCaller(callUnity);

    cleanupUnityBusinessPayloadsExcept(callUnity, businessCleanupKeys, '[UnityMap]')
      .catch(error => {
        console.error('[UnityMap] cleanup stale business models failed', error);
      })
      .finally(() => {
        if (canceled) return;
        onReady?.({ callUnity, videoElement: el });
      });

    return () => {
      canceled = true;
      setActiveUnityBusinessCleanupCaller(null);
    };
  }, [businessCleanupKeys, callUnity, onReady, unityReady]);

  return (
    <div className={`${styles.map} ${className || ''}`}>
      <div ref={unityContainerRef} className={styles.unityBaseMap} />
      {children}
    </div>
  );
};

export default UnityRenderMap;
